import { zodResolver } from '@hookform/resolvers/zod';
import { useNavigate } from '@tanstack/react-router';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

import { CarePlanSummaryPreview } from '@apps/web/components/outreaches/care_plan_summary_preview';
import { MULTI_SHIFT_OUTREACH_MAX } from '@apps/web/components/outreaches/edit_shifts_section';
import { type OutreachCreationAction } from '@apps/web/components/outreaches/manual_outreach_entry';
import { OutreachConflictAlert } from '@apps/web/components/outreaches/outreach_conflict_alert';
import { OutreachStepsPreview } from '@apps/web/components/outreaches/outreach_steps_preview';
import {
    serializeEditedShiftGroups,
    serializeShiftGroups,
    type ShiftGroup,
} from '@apps/web/components/outreaches/shift_group_editor';
import { ClientIdentifyingInfoWarning } from '@apps/web/components/settings/client_identifying_info_warning';
import { Button } from '@apps/web/components/ui/button';
import {
    Form,
    FormControl,
    FormDescription,
    FormField,
    FormItem,
    FormLabel,
    FormMessage,
} from '@apps/web/components/ui/form';
import { Input } from '@apps/web/components/ui/input';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@apps/web/components/ui/select';
import { useClientIdentifyingInfoConfirmation } from '@apps/web/hooks/use_client_identifying_info_confirmation';
import { useOrganizationId } from '@apps/web/hooks/use_organization_id';
import { getApiClient } from '@apps/web/lib/api_client';
import { toast } from '@apps/web/lib/app_notifications';
import { isAutoAssignAllowed } from '@apps/web/lib/auto_assign';
import { useMode } from '@apps/web/lib/mode_store';
import { type OutreachCreationAnalyticsContext } from '@apps/web/lib/outreach_creation_analytics';
import { orderShiftsForOutreach } from '@apps/web/lib/outreach_shift_ordering';
import {
    getChangedOutreachSmsTemplateTexts,
    getDefaultVoiceOpeningLine,
    getEnforceTemplateStepsFromMetadata,
    getTemplateVoiceOpeningLine,
    type IdentifiedOutreachStep,
    normalizeOutreachStepDelays,
    resolveVoiceOpeningLine,
    serializeStepsForSubmission,
    usesSplitVoiceOpeningLine,
} from '@apps/web/lib/outreach_step_editor_utils';
import {
    hasSelectedOutreachConflictShift,
    type OutreachConflictError,
    type OutreachStep,
    parseCancelledShiftSelectionError,
    parseOutreachConflictError,
    showOutreachConflictToast,
} from '@apps/web/lib/outreach_utils';
import {
    useOrganization,
    useOrgAxisCareModifyReasons,
    useOutreaches,
} from '@apps/web/swr_data_fetching/data_fetching_hooks';
import {
    type OutreachTemplate,
    type Shift,
} from '@libraries/typescript/api_client';
import { assert } from '@libraries/typescript/utilities';

const formSchema = z.object({
    outreach_reason_choice: z.string().optional(),
    outreach_reason_other: z.string().optional(),
    openingLine: z.string().optional(),
    smsMessage: z.string().optional(),
    multiShiftHeader: z.string().optional(),
    multiShiftItem: z.string().optional(),
    recurrenceScheduleDescription: z.string().optional(),
});

const MOBILE_CREATE_ACTION_CLASSNAME =
    'w-full touch-manipulation select-none [-webkit-tap-highlight-color:transparent] [-webkit-touch-callout:none] [-webkit-user-select:none] sm:w-auto';

function clearTextSelection() {
    window.getSelection()?.removeAllRanges();
}

type FormValues = z.infer<typeof formSchema>;

const EMPTY_FORM_VALUES: FormValues = {
    outreach_reason_choice: undefined,
    outreach_reason_other: '',
    openingLine: '',
    smsMessage: '',
    multiShiftHeader: '',
    multiShiftItem: '',
    recurrenceScheduleDescription: '',
};

type PerShiftCarePlan = { readonly summary: string; readonly bullets: string };

type ShiftCarePlanMetadata = {
    readonly summary: string | null;
    readonly bullets: string | null;
};

type OutreachMetadata = {
    readonly callout_reason?: string | null;
    readonly original_caregivers?: Record<string, string | null>;
    readonly auto_assign_shifts?: ReadonlyArray<string>;
    readonly auto_assign_notify_non_responders?: boolean;
    readonly auto_assign_modify_reason_id?: number;
    readonly opening_line?: string;
    readonly multi_shift_opening_line?: string;
    readonly recurrence_schedule_description?: string;
    readonly shift_care_plans?: Record<string, ShiftCarePlanMetadata>;
};

// Validate required fields based on active steps for recurring shifts
function validateRecurringShiftFields(
    data: FormValues,
    steps: Array<OutreachStep>,
    setError: (
        name: keyof FormValues,
        error: { type: string; message: string },
    ) => void,
): boolean {
    const hasVoiceStep = steps.some((step) => step.step_type === 'voice');
    if (
        hasVoiceStep &&
        (!data.recurrenceScheduleDescription ||
            data.recurrenceScheduleDescription.trim().length === 0)
    ) {
        setError('recurrenceScheduleDescription', {
            type: 'manual',
            message:
                'Recurrence schedule description is required for recurring shifts',
        });
        return false;
    }
    return true;
}

type EditContext = {
    readonly outreachId: string;
    readonly originalOutreachType: OutreachTemplate['outreach_type'];
    readonly initialIsMultiShift: boolean;
    readonly initialFormValues: FormValues;
    // Carry the persisted step rows (ids + promoted columns) so a v3-born
    // save diffs by id instead of replacing the whole list.
    readonly initialSteps: Array<IdentifiedOutreachStep>;
    readonly initialShiftGroups: ReadonlyArray<ShiftGroup>;
    readonly isV3Born: boolean;
    readonly onSaved?: () => void;
};

type CreateOutreachFormProps = {
    readonly onClose: () => void;
    readonly onSuccess?: (outreachId: string | null) => void;
    readonly navigateOnSuccess: boolean;
    readonly creationAction?: OutreachCreationAction;
    readonly selectedTemplate: OutreachTemplate | undefined;
    readonly selectedShiftIds: Array<string>;
    readonly selectedShifts: Array<Shift>;
    readonly shiftGroups: ReadonlyArray<ShiftGroup> | null;
    readonly carePlanSummary?: string;
    readonly carePlanBullets?: string;
    readonly isLoadingCarePlan?: boolean;
    readonly perShiftCarePlans?: Record<string, PerShiftCarePlan>;
    readonly onCarePlanSummaryChange?: (value: string) => void;
    readonly onCarePlanBulletsChange?: (value: string) => void;
    readonly onPerShiftCarePlanChange?: (
        shiftId: string,
        plan: PerShiftCarePlan,
    ) => void;
    readonly autoAssignShiftIds: ReadonlySet<string> | null;
    readonly autoAssignNotifyNonResponders?: boolean;
    readonly autoAssignModifyReasonId?: number | null;
    readonly supportsAutoAssignFirstResponder: boolean;
    readonly editContext?: EditContext;
    readonly analyticsContext?: OutreachCreationAnalyticsContext;
    readonly exceedsShiftLimit?: boolean;
    readonly selectedShiftCount?: number;
    readonly shiftLimit?: number;
    readonly creationBlockedReason?: string;
    // Called with the offending shift ids when the API rejects the submission
    // because a selected shift is cancelled, so the composer can refetch them.
    readonly onCancelledShiftSelection?: (
        shiftIds: ReadonlyArray<string>,
    ) => void;
};

export type { EditContext as CreateOutreachFormEditContext };

export function CreateOutreachForm({
    onClose,
    onSuccess,
    navigateOnSuccess,
    creationAction,
    selectedTemplate,
    selectedShiftIds,
    selectedShifts,
    shiftGroups,
    carePlanSummary,
    carePlanBullets,
    isLoadingCarePlan,
    perShiftCarePlans,
    onCarePlanSummaryChange,
    onCarePlanBulletsChange,
    onPerShiftCarePlanChange,
    autoAssignShiftIds,
    autoAssignNotifyNonResponders = false,
    autoAssignModifyReasonId = null,
    supportsAutoAssignFirstResponder,
    editContext,
    analyticsContext,
    exceedsShiftLimit,
    selectedShiftCount,
    shiftLimit = MULTI_SHIFT_OUTREACH_MAX,
    creationBlockedReason,
    onCancelledShiftSelection,
}: CreateOutreachFormProps) {
    const isEditMode = editContext !== undefined;
    const outreachType = selectedTemplate?.outreach_type ?? 'callout';
    const isRecurringShift = outreachType === 'recurring_shift';
    const mode = useMode() ?? 'live';
    const submittingLabel = isEditMode
        ? 'Saving…'
        : (creationAction?.submittingLabel ?? 'Creating…');
    const submitLabel = isEditMode
        ? 'Save'
        : (creationAction?.submitLabel ?? 'Draft Outreach');

    const [isSubmitting, setIsSubmitting] = useState(false);
    const [conflictError, setConflictError] =
        useState<OutreachConflictError | null>(null);
    const [conflictFallbackShiftIds, setConflictFallbackShiftIds] = useState<
        ReadonlyArray<string>
    >([]);
    const organizationId = useOrganizationId();
    const { data: organization } = useOrganization(organizationId, {
        reason: 'create-outreach-form',
    });
    const autoAssignAllowed = isAutoAssignAllowed(
        organization?.callout_settings,
    );
    const {
        confirmClientIdentifyingInfo,
        clientIdentifyingInfoConfirmationDialog,
    } = useClientIdentifyingInfoConfirmation({
        clientReferenceTerm:
            organization?.general_settings?.client_reference_term ?? 'client',
        confirmLabel: submitLabel,
    });
    // For AxisCare orgs the Call-Out Reason list is sourced from the account's
    // real AxisCare modification-reason catalog so the chosen reason maps to a
    // real AxisCare code that Phoebe writes back on assignment.
    const isAxisCareOrg =
        organization?.general_settings?.primary_data_source === 'axiscare';
    const { data: axiscareModifyReasonsData } = useOrgAxisCareModifyReasons(
        organizationId,
        { reason: 'create-outreach-form' },
        { enabled: isAxisCareOrg },
    );
    const axiscareReasons = useMemo(
        () => axiscareModifyReasonsData?.reasons ?? [],
        [axiscareModifyReasonsData?.reasons],
    );
    const axiscareReasonIdByLabel = useMemo(() => {
        const map = new Map<string, number>();
        for (const reason of axiscareReasons) {
            map.set(reason.label, reason.id);
        }
        return map;
    }, [axiscareReasons]);
    const navigate = useNavigate();
    const { mutate: mutateOutreaches } = useOutreaches(
        organizationId,
        { reason: 'create-outreach-form' },
        { outreachTypes: ['callout', 'recurring_shift'] },
    );

    const normalizedTemplateSteps = useMemo(
        () =>
            normalizeOutreachStepDelays(
                selectedTemplate?.steps ?? [],
                outreachType,
            ),
        [selectedTemplate?.steps, outreachType],
    );

    const baselineSteps = editContext?.initialSteps ?? normalizedTemplateSteps;

    const [customSteps, setCustomSteps] = useState<
        Array<IdentifiedOutreachStep>
    >(() => {
        if (editContext) {
            return editContext.initialSteps;
        }
        return normalizeOutreachStepDelays(
            selectedTemplate?.steps ?? [],
            outreachType,
        );
    });

    const form = useForm<FormValues>({
        resolver: zodResolver(formSchema),
        defaultValues: editContext?.initialFormValues ?? EMPTY_FORM_VALUES,
        mode: 'onChange',
    });

    const orderedSelectedShifts = useMemo(
        () => orderShiftsForOutreach(selectedShifts, shiftGroups),
        [selectedShifts, shiftGroups],
    );
    const orderedSelectedShiftIds = useMemo(
        () => orderedSelectedShifts.map((shift) => shift.id),
        [orderedSelectedShifts],
    );
    const hasCompleteShiftSelection =
        selectedShiftIds.length > 0 &&
        selectedShifts.length === selectedShiftIds.length;
    const isMultiShift = orderedSelectedShiftIds.length > 1;

    const templateMetadataSchema = selectedTemplate?.metadata_schema as
        | Record<string, unknown>
        | undefined;
    const stepCountLocked = getEnforceTemplateStepsFromMetadata(
        templateMetadataSchema,
    );
    const templateOpeningLine = getTemplateVoiceOpeningLine({
        metadataSchema: templateMetadataSchema,
        outreachType,
        isMultiShift,
    });
    const seededOpeningLineApplies =
        editContext === undefined ||
        editContext.initialIsMultiShift === isMultiShift;
    const effectiveTemplateOpeningLine = seededOpeningLineApplies
        ? editContext?.initialFormValues.openingLine?.trim() ||
          templateOpeningLine
        : templateOpeningLine;

    const outreachReasonOptions: Array<string> = useMemo(() => {
        // AxisCare must never fall back to the generic options while its
        // catalog is loading: those labels have no AxisCare code to write back.
        if (isAxisCareOrg) {
            return axiscareReasons.map((reason) => reason.label);
        }
        return organization?.callout_settings?.options ?? [];
    }, [
        isAxisCareOrg,
        axiscareReasons,
        organization?.callout_settings?.options,
    ]);
    // Pre-populate the Call-Out Reason from the reason already on the visit,
    // only when every selected shift shares one reason; otherwise leave it
    // blank. Tracks the value it auto-applied so it recomputes when the shift
    // selection changes, without clobbering a reason the scheduler picked.
    const autoAppliedReasonRef = useRef<string | null>(null);
    const outreachReasonChoice = form.watch('outreach_reason_choice');
    useEffect(() => {
        if (!isAxisCareOrg || axiscareReasons.length === 0 || isEditMode) {
            return;
        }
        const codes = new Set(
            selectedShifts.map(
                (shift) => shift.external_modification_reason ?? null,
            ),
        );
        const sharedCode = codes.size === 1 ? [...codes][0] : null;
        const effectiveCode =
            codes.size === 1
                ? (sharedCode ??
                  axiscareModifyReasonsData?.default_reason_id ??
                  null)
                : null;
        const sharedLabel =
            effectiveCode !== null
                ? (axiscareReasons.find(
                      (candidate) => candidate.id === Number(effectiveCode),
                  )?.label ?? null)
                : null;
        const current = outreachReasonChoice ?? '';
        const isSchedulerChoice =
            current !== '' &&
            axiscareReasonIdByLabel.has(current) &&
            current !== autoAppliedReasonRef.current;
        if (isSchedulerChoice || (sharedLabel ?? '') === current) {
            return;
        }
        autoAppliedReasonRef.current = sharedLabel;
        form.setValue('outreach_reason_choice', sharedLabel ?? undefined);
    }, [
        isAxisCareOrg,
        axiscareReasons,
        axiscareModifyReasonsData?.default_reason_id,
        axiscareReasonIdByLabel,
        selectedShifts,
        outreachReasonChoice,
        isEditMode,
        form,
    ]);

    // Reset steps + form fields when shift selection changes (not on initial load).
    // In edit mode we keep the pre-populated form values untouched — the user may
    // add/remove shifts without losing their in-flight edits.
    const previousShiftIdsRef = useRef<string>('');
    useEffect(() => {
        const currentKey = selectedShiftIds.join(',');
        if (previousShiftIdsRef.current === currentKey) {
            return;
        }
        const hadPreviousSelection = previousShiftIdsRef.current.length > 0;
        previousShiftIdsRef.current = currentKey;
        setConflictError((currentConflict) =>
            currentConflict &&
            hasSelectedOutreachConflictShift(
                currentConflict,
                selectedShiftIds,
                conflictFallbackShiftIds,
            )
                ? currentConflict
                : null,
        );

        if (isEditMode) {
            return;
        }
        if (hadPreviousSelection) {
            setCustomSteps(normalizedTemplateSteps);
            form.reset(EMPTY_FORM_VALUES, { keepDefaultValues: true });
        }
    }, [
        conflictFallbackShiftIds,
        form,
        isEditMode,
        normalizedTemplateSteps,
        selectedShiftIds,
    ]);

    // Initialize steps when template loads asynchronously after mount.
    // Skip in edit mode — edit mode seeds customSteps from the outreach directly.
    // Tracks which template reference was last applied so that:
    //   - user clearing all steps does NOT re-populate (same template ref),
    //   - switching outreach type / template DOES load the new defaults.
    const lastAppliedTemplateRef = useRef<ReadonlyArray<OutreachStep> | null>(
        customSteps.length > 0 || isEditMode ? normalizedTemplateSteps : null,
    );
    useEffect(() => {
        if (isEditMode) {
            return;
        }
        if (normalizedTemplateSteps.length === 0) {
            return;
        }
        if (lastAppliedTemplateRef.current === normalizedTemplateSteps) {
            return;
        }
        lastAppliedTemplateRef.current = normalizedTemplateSteps;
        setCustomSteps(normalizedTemplateSteps);
    }, [isEditMode, normalizedTemplateSteps]);

    async function onSubmit(data: FormValues) {
        if (isSubmitting) {
            return;
        }
        assert.isNotNull(organizationId);

        if (creationBlockedReason) {
            toast.error(creationBlockedReason, {
                persistNotification: true,
                notificationType: 'outreach_action',
            });
            return;
        }
        if (!selectedTemplate) {
            toast.error('Please select a template', {
                persistNotification: true,
                notificationType: 'outreach_action',
            });
            return;
        }
        if (selectedShiftIds.length === 0) {
            toast.error('Please select at least one shift', {
                persistNotification: true,
                notificationType: 'outreach_action',
            });
            return;
        }
        if (!hasCompleteShiftSelection) {
            toast.error('Please refresh your selection before continuing.', {
                persistNotification: true,
                notificationType: 'outreach_action',
            });
            return;
        }
        if (isRecurringShift) {
            const isValid = validateRecurringShiftFields(
                data,
                customSteps,
                form.setError,
            );
            if (!isValid) {
                return;
            }
        }

        const hasVoiceStep = customSteps.some(
            (step) => step.step_type === 'voice',
        );
        if (hasVoiceStep && !organization?.default_transfer_number) {
            toast.error(
                'Please add a transfer option in Settings > Organization before starting outreach calls!',
                {
                    persistNotification: true,
                    notificationType: 'outreach_action',
                },
            );
            return;
        }

        let composedReason: string | null = null;
        const choice = data.outreach_reason_choice;
        const other = data.outreach_reason_other?.trim();
        if (choice) {
            if (!isAxisCareOrg && choice.toLowerCase() === 'other') {
                if (!other) {
                    form.setError('outreach_reason_other', {
                        type: 'manual',
                        message: 'Please provide a custom reason',
                    });
                    return;
                }
                composedReason = `Other: ${other}`;
            } else {
                composedReason = choice;
            }
        }

        // For AxisCare, the chosen Call-Out Reason maps to a real AxisCare code
        // that is written back on auto-assignment. For other EHRs the reason id
        // still comes from the parent (HHAExchange) selector.
        const resolvedModifyReasonId = isAxisCareOrg
            ? axiscareReasonIdByLabel.get(choice ?? '')
            : (autoAssignModifyReasonId ?? undefined);

        try {
            const {
                trimmedOpeningLine,
                firstVoiceStepIndex,
                hasStoredOpeningLine,
            } = resolveVoiceOpeningLine({
                steps: customSteps,
                templateOpeningLine: effectiveTemplateOpeningLine,
                outreachType,
                isMultiShift,
            });
            const usesMultiShiftVoiceOpeningLine = usesSplitVoiceOpeningLine(
                outreachType,
                isMultiShift,
            );
            // An unset v3 multi-shift line renders derived text; don't persist it.
            const keepsMultiShiftLineUnset =
                editContext?.isV3Born === true &&
                !hasStoredOpeningLine &&
                usesMultiShiftVoiceOpeningLine;
            const singleShiftOpeningLine =
                getTemplateVoiceOpeningLine({
                    metadataSchema: templateMetadataSchema,
                    outreachType,
                    isMultiShift: false,
                })?.trim() ||
                getDefaultVoiceOpeningLine({
                    outreachType,
                    isMultiShift: false,
                }).trim() ||
                null;
            const metadataOpeningLine = usesMultiShiftVoiceOpeningLine
                ? singleShiftOpeningLine
                : trimmedOpeningLine;
            const metadataMultiShiftOpeningLine = usesMultiShiftVoiceOpeningLine
                ? trimmedOpeningLine
                : null;
            const trimmedRecurrenceScheduleDescription =
                data.recurrenceScheduleDescription?.trim();

            const outreach_metadata: OutreachMetadata = {
                callout_reason: composedReason,
                original_caregivers: Object.fromEntries(
                    orderedSelectedShifts.map((shift) => [
                        shift.id,
                        shift.assigned_caregiver_id ?? null,
                    ]),
                ),
                ...(supportsAutoAssignFirstResponder &&
                    autoAssignAllowed &&
                    autoAssignShiftIds !== null && {
                        auto_assign_shifts: [...autoAssignShiftIds],
                        auto_assign_notify_non_responders:
                            autoAssignShiftIds.size > 0 &&
                            autoAssignNotifyNonResponders,
                        ...(autoAssignShiftIds.size > 0 &&
                            resolvedModifyReasonId !== undefined && {
                                auto_assign_modify_reason_id:
                                    resolvedModifyReasonId,
                            }),
                    }),
                ...(metadataOpeningLine && {
                    opening_line: metadataOpeningLine,
                }),
                ...(metadataMultiShiftOpeningLine && {
                    multi_shift_opening_line: metadataMultiShiftOpeningLine,
                }),
                ...(trimmedRecurrenceScheduleDescription && {
                    recurrence_schedule_description:
                        trimmedRecurrenceScheduleDescription,
                }),
                ...(perShiftCarePlans &&
                    Object.keys(perShiftCarePlans).length > 0 && {
                        shift_care_plans: Object.fromEntries(
                            Object.entries(perShiftCarePlans).map(
                                ([id, cp]) => [
                                    id,
                                    {
                                        summary: cp.summary || null,
                                        bullets: cp.bullets || null,
                                    },
                                ],
                            ),
                        ),
                    }),
            };

            const serializedSteps = serializeStepsForSubmission({
                steps: customSteps,
                outreachType,
                trimmedOpeningLine: keepsMultiShiftLineUnset
                    ? null
                    : trimmedOpeningLine,
                firstVoiceStepIndex,
                isMultiShift,
                includeStepIds: editContext?.isV3Born === true,
            });

            if (
                !(await confirmClientIdentifyingInfo(
                    getChangedOutreachSmsTemplateTexts(
                        serializedSteps,
                        baselineSteps,
                    ),
                ))
            ) {
                return;
            }

            setConflictError(null);
            setConflictFallbackShiftIds([]);
            setIsSubmitting(true);
            if (editContext) {
                const typeChanged =
                    outreachType !== editContext.originalOutreachType;
                await getApiClient().patch({
                    url: '/organizations/{organization_id}/callouts/{callout_id}',
                    pathParams: {
                        organization_id: organizationId,
                        callout_id: editContext.outreachId,
                    },
                    body: {
                        callout_reason: composedReason,
                        ...(keepsMultiShiftLineUnset
                            ? {}
                            : { opening_line: trimmedOpeningLine || null }),
                        recurrence_schedule_description:
                            trimmedRecurrenceScheduleDescription || null,
                        ...(supportsAutoAssignFirstResponder &&
                            autoAssignAllowed &&
                            autoAssignShiftIds !== null && {
                                auto_assign_shifts: [...autoAssignShiftIds],
                            }),
                        shift_care_plans:
                            perShiftCarePlans &&
                            Object.keys(perShiftCarePlans).length > 0
                                ? Object.fromEntries(
                                      Object.entries(perShiftCarePlans).map(
                                          ([id, cp]) => [
                                              id,
                                              {
                                                  summary: cp.summary || null,
                                                  bullets: cp.bullets || null,
                                              },
                                          ],
                                      ),
                                  )
                                : null,
                        steps: serializedSteps,
                        shift_ids: orderedSelectedShiftIds,
                        // `null` means the outreach's groups never loaded.
                        ...(shiftGroups !== null && {
                            shift_groups: serializeEditedShiftGroups(
                                shiftGroups,
                                editContext.initialShiftGroups,
                            ),
                        }),
                        ...(typeChanged && {
                            outreach_type: outreachType,
                            base_prompt: selectedTemplate.base_prompt,
                            output_schema: selectedTemplate.output_schema,
                        }),
                    },
                });
                mutateOutreaches();
                editContext.onSaved?.();
                toast.success('Outreach updated', {
                    persistNotification: true,
                    notificationType: 'outreach_action',
                });
                onClose();
                onSuccess?.(editContext.outreachId);
                return;
            }

            const response = await getApiClient().post({
                url: '/organizations/{organization_id}/outreaches',
                mode,
                pathParams: { organization_id: organizationId },
                body: {
                    outreach_type: outreachType,
                    shift_ids: orderedSelectedShiftIds,
                    base_prompt: selectedTemplate.base_prompt,
                    outreach_metadata,
                    output_schema: selectedTemplate.output_schema,
                    steps: serializedSteps,
                    // `null` keeps the server's same-client default grouping;
                    // `[]` records that the coordinator cleared every group.
                    // `serializeShiftGroups` returns undefined for both, and
                    // JSON drops it, so the distinction is rebuilt here.
                    shift_groups:
                        shiftGroups === null
                            ? null
                            : (serializeShiftGroups(shiftGroups) ?? []),
                    ...(analyticsContext && {
                        analytics_context: analyticsContext,
                    }),
                },
            });
            mutateOutreaches();
            if (creationAction) {
                await creationAction.afterCreate(response.id);
            } else {
                toast.success('Outreach created successfully', {
                    persistNotification: true,
                    notificationType: 'outreach_action',
                });
            }
            form.reset();
            onClose();
            const outreachId = response?.id ?? null;
            onSuccess?.(outreachId);
            if (navigateOnSuccess && outreachId) {
                navigate({
                    to: '/outreaches/$callout-id',
                    params: { 'callout-id': outreachId },
                    search: { openChat: true },
                });
            }
        } catch (error) {
            console.error(
                isEditMode
                    ? 'Error updating outreach:'
                    : 'Error creating outreach:',
                error,
            );
            const cancelledSelection = parseCancelledShiftSelectionError(error);
            if (cancelledSelection) {
                // Our shift data is stale: the shift was cancelled after the
                // composer loaded it. Refetch so the row is flagged and the
                // submit stays locked until it is removed.
                onCancelledShiftSelection?.(cancelledSelection.shiftIds);
                toast.error(cancelledSelection.message, {
                    persistNotification: true,
                    notificationType: 'outreach_action',
                });
                return;
            }
            const conflict = parseOutreachConflictError(error);
            if (conflict) {
                setConflictFallbackShiftIds(orderedSelectedShiftIds);
                setConflictError(conflict);
                showOutreachConflictToast(conflict, (outreachId) =>
                    navigate({
                        to: '/outreaches/$callout-id',
                        params: { 'callout-id': outreachId },
                    }),
                );
            } else {
                toast.error(
                    isEditMode
                        ? 'Failed to update outreach'
                        : 'Failed to create outreach',
                    {
                        persistNotification: true,
                        notificationType: 'outreach_action',
                    },
                );
            }
        } finally {
            setIsSubmitting(false);
        }
    }

    const hasSmsStep = customSteps.some((step) => step.step_type === 'sms');
    const hasVoiceStep = customSteps.some((step) => step.step_type === 'voice');
    const showCarePlanPreview =
        !!carePlanBullets ||
        !!carePlanSummary ||
        !!isLoadingCarePlan ||
        (perShiftCarePlans && Object.keys(perShiftCarePlans).length > 0);

    const canSubmit = !!selectedTemplate;
    const isSubmitDisabled =
        isSubmitting ||
        !canSubmit ||
        !hasCompleteShiftSelection ||
        Boolean(isLoadingCarePlan) ||
        Boolean(creationBlockedReason) ||
        Boolean(exceedsShiftLimit);
    const submitDisabledReason = !canSubmit
        ? null
        : !hasCompleteShiftSelection
          ? selectedShiftIds.length === 0
              ? 'Select at least one shift to continue.'
              : 'Loading selected shift details…'
          : isLoadingCarePlan
            ? 'Preparing care plan details…'
            : null;

    return (
        <React.Fragment>
            <Form {...form}>
                <form
                    onSubmit={form.handleSubmit(onSubmit)}
                    className="flex min-h-full min-w-0 flex-col overflow-x-hidden max-sm:pb-24"
                >
                    <div className="min-w-0 space-y-6 pb-4">
                        {conflictError && (
                            <OutreachConflictAlert
                                conflict={conflictError}
                                timezone={organization?.timezone}
                            />
                        )}
                        {canSubmit ? (
                            <React.Fragment>
                                {showCarePlanPreview && (
                                    <CarePlanSummaryPreview
                                        isLoading={isLoadingCarePlan}
                                        carePlanSummary={carePlanSummary}
                                        carePlanBullets={carePlanBullets}
                                        perShiftCarePlans={perShiftCarePlans}
                                        selectedShifts={orderedSelectedShifts}
                                        timezone={organization?.timezone}
                                        showSms={hasSmsStep}
                                        showVoice={hasVoiceStep}
                                        onCarePlanSummaryChange={
                                            onCarePlanSummaryChange
                                        }
                                        onCarePlanBulletsChange={
                                            onCarePlanBulletsChange
                                        }
                                        onPerShiftCarePlanChange={
                                            onPerShiftCarePlanChange
                                        }
                                    />
                                )}
                                <FormField
                                    control={form.control}
                                    name="outreach_reason_choice"
                                    render={({ field }) => (
                                        <FormItem>
                                            <FormLabel>
                                                Call-Out Reason
                                            </FormLabel>
                                            <FormControl>
                                                <Select
                                                    value={field.value}
                                                    onValueChange={(value) => {
                                                        field.onChange(value);
                                                        if (
                                                            isAxisCareOrg ||
                                                            value.toLowerCase() !==
                                                                'other'
                                                        ) {
                                                            form.setValue(
                                                                'outreach_reason_other',
                                                                '',
                                                            );
                                                        }
                                                    }}
                                                >
                                                    <SelectTrigger className="w-full">
                                                        <SelectValue placeholder="Select a reason" />
                                                    </SelectTrigger>
                                                    <SelectContent>
                                                        {outreachReasonOptions.map(
                                                            (option) => (
                                                                <SelectItem
                                                                    key={option}
                                                                    value={
                                                                        option
                                                                    }
                                                                >
                                                                    {option}
                                                                </SelectItem>
                                                            ),
                                                        )}
                                                    </SelectContent>
                                                </Select>
                                            </FormControl>
                                            <FormDescription>
                                                {isAxisCareOrg
                                                    ? 'Optional: Choose an AxisCare Open Shift Reason.'
                                                    : 'Optional: Choose a reason or select “Other” to type a custom one.'}
                                            </FormDescription>
                                            <FormMessage />
                                        </FormItem>
                                    )}
                                />
                                {!isAxisCareOrg &&
                                    form
                                        .watch('outreach_reason_choice')
                                        ?.toLowerCase() === 'other' && (
                                        <FormField
                                            control={form.control}
                                            name="outreach_reason_other"
                                            render={({ field }) => (
                                                <FormItem>
                                                    <FormLabel>
                                                        Describe the reason
                                                    </FormLabel>
                                                    <FormControl>
                                                        <Input
                                                            placeholder="Please describe"
                                                            {...field}
                                                        />
                                                    </FormControl>
                                                    <FormMessage />
                                                </FormItem>
                                            )}
                                        />
                                    )}
                                <OutreachStepsPreview
                                    steps={customSteps}
                                    selectedShiftIds={orderedSelectedShiftIds}
                                    selectedShifts={orderedSelectedShifts}
                                    organization={organization}
                                    outreachType={outreachType}
                                    form={form}
                                    isLoadingInstructions={false}
                                    onStepsChange={setCustomSteps}
                                    templateSteps={normalizedTemplateSteps}
                                    originalSteps={normalizedTemplateSteps}
                                    stepCountLocked={stepCountLocked}
                                    perShiftCarePlans={perShiftCarePlans}
                                    templateOpeningLine={
                                        effectiveTemplateOpeningLine ??
                                        undefined
                                    }
                                    structuredShiftGroups={shiftGroups}
                                />
                            </React.Fragment>
                        ) : (
                            <p className="text-muted-foreground text-sm">
                                Select a template to continue.
                            </p>
                        )}
                    </div>
                    {exceedsShiftLimit && (
                        <p className="text-destructive text-right text-sm font-medium">
                            You have selected {selectedShiftCount} shifts.
                            Please reduce to {shiftLimit} or fewer to continue.
                        </p>
                    )}
                    {submitDisabledReason && !exceedsShiftLimit && (
                        <p className="text-muted-foreground text-right text-sm">
                            {submitDisabledReason}
                        </p>
                    )}
                    {creationBlockedReason && !exceedsShiftLimit && (
                        <p
                            id="create-outreach-blocked-reason"
                            className="text-destructive text-right text-sm font-medium"
                            role="alert"
                        >
                            {creationBlockedReason}
                        </p>
                    )}
                    <ClientIdentifyingInfoWarning
                        templates={getChangedOutreachSmsTemplateTexts(
                            customSteps,
                            baselineSteps,
                        )}
                    />
                    <div
                        data-testid="create-outreach-form-actions"
                        className="bg-background sticky bottom-0 z-10 mt-auto flex flex-col-reverse gap-2 border-t py-3 select-none [-webkit-tap-highlight-color:transparent] [-webkit-touch-callout:none] [-webkit-user-select:none] max-sm:fixed max-sm:inset-x-0 max-sm:bottom-0 max-sm:z-[60] max-sm:px-4 max-sm:pb-[calc(0.75rem+env(safe-area-inset-bottom))] sm:flex-row sm:justify-end"
                    >
                        <Button
                            type="button"
                            variant="outline"
                            onClick={onClose}
                            disabled={isSubmitting}
                            className={MOBILE_CREATE_ACTION_CLASSNAME}
                            onPointerUp={clearTextSelection}
                        >
                            Cancel
                        </Button>
                        <Button
                            type="submit"
                            disabled={isSubmitDisabled}
                            // Without this the button is disabled with no
                            // programmatic link to the reason, so a screen
                            // reader reaches a dead end.
                            aria-describedby={
                                creationBlockedReason && !exceedsShiftLimit
                                    ? 'create-outreach-blocked-reason'
                                    : undefined
                            }
                            className={MOBILE_CREATE_ACTION_CLASSNAME}
                            onClick={clearTextSelection}
                            onPointerUp={clearTextSelection}
                        >
                            {isSubmitting ? submittingLabel : submitLabel}
                        </Button>
                    </div>
                </form>
            </Form>
            {clientIdentifyingInfoConfirmationDialog}
        </React.Fragment>
    );
}
